import { Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Socket } from "socket.io-client";

import { useUpdateStatusRequestMoney } from "../../../../api/send-money";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { ButtonOutlinedCustom } from "../../../../components/button/buttonOulined";
import { purposeOfRemittanceItemNames } from "../../../../components/inputs/select/purposeOfRemittance.constants";
import ConfirmModal from "../../../../components/modal/confirmModal";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import { Fiat } from "../../../../pages/request-money/component/form-transfer-props";
import PaymentStatus from "../../../../types/paymentStatus.type";
import useResponsive from "../../../../utils/hooks/useResponsive";
import StatusForm from "./status-form";
import TextContent from "./text-content";

const FormRequestMoney = ({
  orderId,
  refetch,
  socket,
  data,
  refetchDetail,
}: {
  orderId: string;
  refetch: any;
  socket: Socket<any, any>;
  data: any;
  refetchDetail: any;
}) => {
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const [idCancel, setIdCancel] = useState(null);

  const { mutate: updateRequestMoney, isLoading: isLoadingUpdate } =
    useUpdateStatusRequestMoney({
      onSuccess: () => {
        refetchDetail();
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const { mutate: cancelSendMoney, isLoading: isLoadingCancel } =
    useUpdateStatusRequestMoney({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification({
            key: "update_success",
            message: "Request Money order canceled successfully!",
            type: "success",
          });
        }
        refetch();
        refetchDetail();
        setIdCancel(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const handlePaid = () => {
    updateRequestMoney({
      id: Number(orderId),
      isPaid: true,
    });
  };

  const handleSubmitCancel = () => {
    if (isLoadingCancel) return;
    cancelSendMoney({
      id: Number(orderId),
      status: "CANCELLED",
    });
  };

  const handleCancel = () => {
    if (isLoadingCancel) return;
    setIdCancel(null);
  };

  const reasonStatus = () => {
    switch (data?.data?.status?.current) {
      case PaymentStatus.PENDING:
        // return "To finalize your order, please complete the payment as instructed.";
        return "";
      case PaymentStatus.REJECTED:
        return data?.data?.reason || "N/A";
      case PaymentStatus.POSTPONED:
        return data?.data?.reason || "N/A";
      case PaymentStatus.FAILED:
        return data?.data?.reason || "Something went wrong.";
      case PaymentStatus.APPROVED:
        return "This request was approved by the admin.";
      case PaymentStatus.CANCELLED:
        return "Request money order canceled successfully.";
      case PaymentStatus.COMPLETED:
        return "Request money has been completed. Check your portfolio for details.";
      default:
        return "";
    }
  };

  const renderButton = () => {
    const status = data?.data?.status?.current;
    const isPaid = data?.data?.isPaid;
    const paymentId = data?.data?.id;

    const shouldRender = !(
      (status !== PaymentStatus.PENDING && isPaid) ||
      status === PaymentStatus.CANCELLED ||
      status === PaymentStatus.COMPLETED
    );

    return shouldRender ? (
      <div className="form-history-button">
        {!isPaid && status !== PaymentStatus.COMPLETED ? (
          <CustomButton
            size={isTablet ? "medium" : "large"}
            text="Confirm"
            width="100%"
            onClick={handlePaid}
            isLoading={isLoadingUpdate || isLoadingCancel}
          />
        ) : null}
        {status === PaymentStatus.PENDING || status === PaymentStatus.FAILED ? (
          <ButtonOutlinedCustom
            size={isTablet ? "medium" : "large"}
            text="Cancel Request Money"
            width="100%"
            className="outline-dangerous"
            isLoading={isLoadingUpdate || isLoadingCancel}
            onClick={() => setIdCancel(paymentId)}
          />
        ) : null}
      </div>
    ) : null;
  };

  useEffect(() => {
    if (!orderId) return;
    const handleDetailUpdate = () => {
      refetchDetail();
    };
    socket.on(`user.paymentOrders.${orderId}.updated`, handleDetailUpdate);

    return () => {
      socket.off(`user.paymentOrders.${orderId}.updated`, handleDetailUpdate);
    };
  }, [socket, orderId, refetchDetail]);

  return (
    <div>
      <div className="form-history">
        <StatusForm
          status={data?.data?.status?.current}
          content={reasonStatus()}
        />
        <div className="modal-balance">
          <div className="value-balance">
            <Typography style={{ fontSize: 16, color: "white" }}>
              {data?.data?.status?.current === PaymentStatus.COMPLETED ||
                data?.data?.isPaid
                ? "Paid"
                : "Total to pay"}
            </Typography>
            <Typography className="text-gradient">
              {data?.data?.amounts?.totalSendAmount}{" "}
              <Typography.Text
                className="text-gradient"
                style={{
                  fontSize: isTablet ? "20px" : "24px",
                }}
              >
                {data?.data?.paymentCurrency}
              </Typography.Text>
            </Typography>
          </div>
        </div>
        <div className="form-history-content">
          <TextContent
            label="Beneficiary Will Receive"
            value={`${data?.data?.amounts?.totalReceiveAmount} ${data?.data?.receiveCurrency}`}
            color="rgba(242, 138, 40, 1)"
          />
        </div>
        <div className="form-history-content border-top">
          <TextContent label="Payment Method" value="Provider" />
          <TextContent
            label="Provider"
            value={data?.data?.serviceProvider?.name}
            color="rgba(242, 138, 40, 1)"
          />
          <TextContent
            label="Currency"
            value={data?.data?.receiveCurrency}
            canCopy
          />
          {Fiat.includes(data?.data?.paymentCurrency) ? (
            <>
              <TextContent
                label="Provider Bank Name"
                value={data?.data?.serviceProvider?.providerBankName}
                canCopy
              />
              <TextContent
                label="Provider Bank SWIFT"
                value={data?.data?.serviceProvider?.providerBankSwift}
                canCopy
              />
              <TextContent
                label="Provider Bank Address"
                value={data?.data?.serviceProvider?.providerBankAddress}
                canCopy
              />
              <TextContent
                label="Provider Bank Account"
                value={data?.data?.serviceProvider?.providerBankAccount}
                canCopy
              />
            </>
          ) : (
            <>
              <TextContent
                label="Network"
                value={data?.data?.serviceProvider?.network}
                canCopy
              />
              <TextContent
                label="Provider Wallet Address"
                value={data?.data?.serviceProvider?.walletAddress}
                canCopy
              />
            </>
          )}
          <TextContent
            label="Reference Number"
            value={data?.data?.referenceNumber}
            canCopy
            color="rgba(242, 138, 40, 1)"
          />
          <TextContent
            label="Purpose of Remittance"
            value={data?.data?.purposeOfRemittance &&
              purposeOfRemittanceItemNames[
              data?.data
                ?.purposeOfRemittance as keyof typeof purposeOfRemittanceItemNames
              ]
            }
          />
          {/* <TextContent
            label="Send Date"
            value={moment(data?.data?.timestamps?.dateOfTransaction).format(
              "DD/MM/YYYY"
            )}
          /> */}
          {data?.data?.futurePaymentDate ? (
            <TextContent
              label="Future date"
              value={data?.data?.futurePaymentDate
                ? moment(data?.data?.futurePaymentDate).format("DD/MM/YYYY")
                : ""
              }
            />
          ) : (
            <></>
          )}
        </div>
        <div className="form-history-content border-top">
          <TextContent
            label="Order Time"
            value={moment(data?.data?.timestamps?.orderTime).format(
              "HH:mm:ss DD/MM/YYYY"
            )}
          />
        </div>
      </div>
      {renderButton()}
      <ConfirmModal
        type="warning"
        title="Cancel request money?"
        description="Are you sure you want to cancel this request money order? This action cannot be undone."
        open={!!idCancel}
        onCancel={handleCancel}
        onSubmit={handleSubmitCancel}
        isLoading={false}
      />
    </div>
  );
};

export default FormRequestMoney;
